// TraitementDivers.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    Download,
    MessageSquare,
    Loader,
} from "lucide-react";

import "../TraitementStyles/TraitementAvances.css";

import {
    getManagerAttestationInboxRequest,
    getAdminAttestationInboxRequest,
    managerAttestationDecisionRequest,
    adminAttestationDecisionRequest,
    downloadAttestationRequestPdfRequest,
    clearAttestationsMessage,
} from "../../../../redux/actions/attestationsActions";

const TraitementDivers = () => {
    const dispatch = useDispatch();

    const currentRole = useSelector((state) => state.role?.selectedRole);
    const roleCode = currentRole?.code;
    const isManager  = roleCode === "GRH_MANAGER";
    const isAdminRH  = !isManager && roleCode === "ADMIN_GRH";

    const attState   = useSelector((state) => state.attestations || {});
    const managerInbox = useSelector((state) => state.attestations.managerInbox || []);
    const adminInbox   = useSelector((state) => state.attestations.adminInbox   || []);
    const loading    = !!attState.loading;
    const apiError   = attState.error   || "";
    const apiMessage = attState.message || "";

    const [activeTab,        setActiveTab]        = useState("enCours");
    const [searchTerm,       setSearchTerm]        = useState("");
    const [showFilters,      setShowFilters]        = useState(false);
    const [showRejectModal,  setShowRejectModal]    = useState(false);
    const [showDetailsModal, setShowDetailsModal]   = useState(false);
    const [selectedDemande,  setSelectedDemande]    = useState(null);
    const [motifRejet,       setMotifRejet]         = useState("");

    const [filters, setFilters] = useState({
        typeAttestation: "",
        departement:     "",
        dateDebut:       "",
        dateFin:         "",
    });

    // Charger les données au montage
    useEffect(() => {
        if (isManager)      dispatch(getManagerAttestationInboxRequest());
        else if (isAdminRH) dispatch(getAdminAttestationInboxRequest());
    }, [isManager, isAdminRH]);

    useEffect(() => {
        if (apiError || apiMessage) {
            const t = setTimeout(() => dispatch(clearAttestationsMessage()), 1500);
            return () => clearTimeout(t);
        }
    }, [apiError, apiMessage]);

    const rawDemandes = isManager ? managerInbox : adminInbox;

    const formatDate = (d) => {
        if (!d) return "N/A";
        const dt = new Date(d);
        if (Number.isNaN(dt.getTime())) return String(d).slice(0, 10);
        return dt.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
    };

    const mapStatusToTab = (statusRaw) => {
        const s = String(statusRaw || "").toUpperCase();
        if (s === "APPROVED") return "approuve";
        if (s === "REJECTED") return "rejete";
        return "enCours";
    };

    const getStatutBadge = (statut) => {
        const config = {
            enCours:  { label: "En attente", bg: "#fff3cd", color: "#856404" },
            approuve: { label: "Approuvé",   bg: "#d4edda", color: "#155724" },
            rejete:   { label: "Rejeté",     bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.enCours;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    // ── Tabs counts ──────────────────────────────────────────────────────────
    const tabs = useMemo(() => {
        if (!rawDemandes) return [
            { id: "enCours",  label: "En cours",    count: 0 },
            { id: "approuve", label: "Approuvées",  count: 0 },
            { id: "rejete",   label: "Rejetées",    count: 0 },
        ];
        return [
            { id: "enCours",  label: "En cours",   count: rawDemandes.filter((d) => mapStatusToTab(d.status || d.statut) === "enCours").length  },
            { id: "approuve", label: "Approuvées", count: rawDemandes.filter((d) => mapStatusToTab(d.status || d.statut) === "approuve").length },
            { id: "rejete",   label: "Rejetées",   count: rawDemandes.filter((d) => mapStatusToTab(d.status || d.statut) === "rejete").length   },
        ];
    }, [rawDemandes]);

    // ── Filter options ────────────────────────────────────────────────────────
    const typesAttestation = useMemo(() => {
        return [...new Set((rawDemandes || []).map((d) => d.attestationTitre || d.attestation?.titre || d.attestation?.nom).filter(Boolean))];
    }, [rawDemandes]);

    const departements = useMemo(() => {
        return [...new Set((rawDemandes || []).map((d) => d.departement || d.department || d.departementNom).filter(Boolean))];
    }, [rawDemandes]);

    // ── Filtered list ─────────────────────────────────────────────────────────
    const getFilteredDemandes = () => {
        if (!rawDemandes || rawDemandes.length === 0) return [];

        const toStr = (v) => (v === null || v === undefined ? "" : String(v));

        let mapped = rawDemandes.map((d) => {
            const att = d.attestation || {};
            const statut = mapStatusToTab(d.status || d.statut);
            return {
                id:              d.id,
                matricule:       d.matricule || d.code || "—",
                collaborateur:   d.fullName || d.nomComplet || `${d.employeeNom || ""} ${d.employeePrenom || ""}`.trim() || "—",
                poste:           d.poste || d.jobTitle || d.posteNom || "—",
                departement:     d.departement || d.department || d.departementNom || "—",
                typeAttestation: d.attestationTitre || att.titre || att.nom || "Attestation",
                couleurType:     d.couleurType || att.couleur || "#3b82f6",
                dateDemande:     d.createdAt || d.dateDemande || "",
                motif:           d.motif || d.commentaire || "",
                typeDomiciliation:       d.typeDomiciliation || "",
                montantDomiciliation:    d.montantDomiciliation,
                pourcentageDomiciliation:d.pourcentageDomiciliation,
                moisDemandes:    d.moisDemandes || d.moisSelectionnes || null,
                documentsJoints: d.attachments || d.documentsJoints || [],
                managerComment:  d.managerComment || "",
                adminComment:    d.adminComment || "",
                statut,
                canDownload:     statut === "approuve",
            };
        });

        // Tab filter
        mapped = mapped.filter((d) => d.statut === activeTab);

        // Search
        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            mapped = mapped.filter((d) =>
                [d.collaborateur, d.matricule, d.departement, d.poste, d.typeAttestation, d.motif]
                    .join(" ").toLowerCase().includes(s)
            );
        }

        if (filters.typeAttestation) mapped = mapped.filter((d) => d.typeAttestation === filters.typeAttestation);
        if (filters.departement)     mapped = mapped.filter((d) => d.departement     === filters.departement);
        if (filters.dateDebut)       mapped = mapped.filter((d) => String(d.dateDemande).slice(0, 10) >= filters.dateDebut);
        if (filters.dateFin)         mapped = mapped.filter((d) => String(d.dateDemande).slice(0, 10) <= filters.dateFin);

        return mapped;
    };

    const filtered = getFilteredDemandes();

    // ── Actions ───────────────────────────────────────────────────────────────
    const handleApprove = (demandeId) => {
        if (!demandeId) return;
        const payload = { id: demandeId, approved: true, comment: "" };
        if (isManager)      dispatch(managerAttestationDecisionRequest(payload));
        else if (isAdminRH) dispatch(adminAttestationDecisionRequest(payload));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setMotifRejet("");
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!selectedDemande?.id || !motifRejet.trim()) return;
        const payload = { id: selectedDemande.id, approved: false, comment: motifRejet };
        if (isManager)      dispatch(managerAttestationDecisionRequest(payload));
        else if (isAdminRH) dispatch(adminAttestationDecisionRequest(payload));
        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    const handleDownload = (demande) => {
        if (!demande?.id) return;
        dispatch(downloadAttestationRequestPdfRequest({ requestId: demande.id, filename: `${demande.typeAttestation}.pdf` }));
    };

    // ── Loading state ─────────────────────────────────────────────────────────
    if (loading && (!rawDemandes || rawDemandes.length === 0)) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader">
                    <div>
                        <h1>Traitement des demandes d'attestations</h1>
                        <p>Gérez et validez les demandes d'attestations de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tavCard">
                    <div className="tavControls">
                        <div className="tavSearch">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, département, type d'attestation..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tavFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16} />
                            Filtres
                            <ChevronDown size={16} className={`tavChevron ${showFilters ? "is-open" : ""}`} />
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tavFilters">
                            <div className="tavField">
                                <label>Type d'attestation</label>
                                <select value={filters.typeAttestation} onChange={(e) => setFilters((p) => ({ ...p, typeAttestation: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesAttestation.map((t) => (
                                        <option key={t} value={t}>{t}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tavField">
                                <label>Département</label>
                                <select value={filters.departement} onChange={(e) => setFilters((p) => ({ ...p, departement: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {departements.map((d) => (
                                        <option key={d} value={d}>{d}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="tavField">
                                <label>Date début</label>
                                <input type="date" value={filters.dateDebut} onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))} />
                            </div>

                            <div className="tavField">
                                <label>Date fin</label>
                                <input type="date" value={filters.dateFin} onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs + Table */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Département</th>
                                    <th>Type d'attestation</th>
                                    <th>Date demande</th>
                                    <th>Motif</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        onClick={() => showDetails(demande)}
                                        title="Voir les détails"
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.matricule}</span>
                                        </td>

                                        <td>
                                            <div className="tavPrimary">{demande.collaborateur}</div>
                                            <div className="tavSub">{demande.poste}</div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{demande.departement}</span>
                                        </td>

                                        <td>
                                            <div className="tavType">
                                                <span className="tavDot" style={{ backgroundColor: demande.couleurType }} />
                                                <div>
                                                    <div className="tavPrimary">{demande.typeAttestation}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{formatDate(demande.dateDemande)}</span>
                                        </td>

                                        <td>
                                            <div className="tavTrunc" title={demande.motif}>
                                                {demande.motif || "—"}
                                            </div>
                                        </td>

                                        <td>{getStatutBadge(demande.statut)}</td>

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {activeTab === "enCours" ? (
                                                <div className="tavRowActions">
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--ok"
                                                        onClick={() => handleApprove(demande.id)}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--no"
                                                        onClick={() => handleReject(demande)}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="tavRowActions">
                                                    <button
                                                        type="button"
                                                        className="tavDetailsBtn"
                                                        onClick={() => showDetails(demande)}
                                                    >
                                                        Détails
                                                    </button>
                                                    {demande.canDownload && (
                                                        <button
                                                            type="button"
                                                            className="tavActionBtn tavActionBtn--dl"
                                                            title="Télécharger"
                                                            onClick={() => handleDownload(demande)}
                                                        >
                                                            <Download size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* ── DETAILS MODAL ─────────────────────────────────────────────── */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <FileText size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demande du {formatDate(selectedDemande.dateDemande)}</div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Infos collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Informations collaborateur
                                    </div>
                                    <div className="tavKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.matricule}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Nom complet</span>
                                        <b>{selectedDemande.collaborateur}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Poste</span>
                                        <b>{selectedDemande.poste}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Département</span>
                                        <b>{selectedDemande.departement}</b>
                                    </div>
                                </div>

                                {/* Détails demande */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Détails de la demande
                                    </div>
                                    <div className="tavKv">
                                        <span>Type d'attestation</span>
                                        <b>{selectedDemande.typeAttestation}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Date de demande</span>
                                        <b>{formatDate(selectedDemande.dateDemande)}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Statut</span>
                                        <b>{getStatutBadge(selectedDemande.statut)}</b>
                                    </div>
                                    {selectedDemande.typeDomiciliation && (
                                        <div className="tavKv">
                                            <span>Domiciliation</span>
                                            <b className="tavAccent">
                                                {String(selectedDemande.typeDomiciliation).toLowerCase() === "percentage"
                                                    ? `${selectedDemande.pourcentageDomiciliation || 0}% du salaire`
                                                    : `${Number(selectedDemande.montantDomiciliation || 0).toLocaleString()} MAD`}
                                            </b>
                                        </div>
                                    )}
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <MessageSquare size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || "Non précisé"}</div>
                                </div>

                                {/* Mois demandés */}
                                {Array.isArray(selectedDemande.moisDemandes) && selectedDemande.moisDemandes.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <Clock size={16} /> Mois demandés
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.moisDemandes.map((mois, idx) => (
                                                <span key={idx} className="tavTag">{mois}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Documents joints */}
                                {Array.isArray(selectedDemande.documentsJoints) && selectedDemande.documentsJoints.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <Download size={16} /> Documents joints
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.documentsJoints.map((doc, idx) => (
                                                <span key={idx} className="tavTag">
                                                    {typeof doc === "string" ? doc.split("/").pop() : doc?.name || "Document"}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision (si déjà traitée) */}
                                {selectedDemande.statut !== "enCours" && (
                                    <div className={`tavPanel tavPanel--full ${selectedDemande.statut === "approuve" ? "tavValidOk" : "tavValidNo"}`}>
                                        <div className="tavPanel__title">
                                            {selectedDemande.statut === "approuve" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.statut === "approuve" ? " Demande approuvée" : " Demande rejetée"}
                                        </div>
                                        {(selectedDemande.managerComment || selectedDemande.adminComment) && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">
                                                    {selectedDemande.statut === "approuve" ? "Commentaire" : "Motif du rejet"}
                                                </div>
                                                <div className="tavRejectText">
                                                    {selectedDemande.managerComment || selectedDemande.adminComment}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="tavModal__footer">
                            {selectedDemande.statut === "enCours" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => { closeDetails(); handleReject(selectedDemande); }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => { handleApprove(selectedDemande.id); closeDetails(); }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>
                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <>
                                    {selectedDemande.canDownload && (
                                        <button type="button" className="tavBtn tavBtn--dl" onClick={() => handleDownload(selectedDemande)}>
                                            <Download size={16} />
                                            Télécharger
                                        </button>
                                    )}
                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* ── REJECT MODAL ──────────────────────────────────────────────── */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Refuser la demande d'attestation</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.collaborateur} — {selectedDemande.typeAttestation}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Type d'attestation</span>
                                    <b className="tavPrimary">{selectedDemande.typeAttestation}</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Collaborateur</span>
                                    <b>{selectedDemande.collaborateur}</b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                Motif du rejet <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette demande..."
                                className="tavTextarea"
                            />
                            <div className="tavHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>
                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementDivers;